import React, { FC, useCallback, useMemo } from 'react';
import get from 'lodash/get';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import NotificationsPage from 'components/notificationsPage';
import { NotificationsContextProvider } from 'components/context/NotificationsContext';

import { onError } from 'graphql/helpers';
import { toastNotify } from 'graphql/hooks/common';
import {
  decreaseEventNotifsCount,
  increaseEventNotifsCount,
  removeEventCustomNotifiaction,
  updateEventNotif,
  updateNotifSentCache,
} from 'graphql/cache/notifications';

import { UPDATE_EVENT } from 'graphql/mutations/events';
import { GET_EVENT_CUSTOM_NOTIFICATIONS } from 'graphql/queries/notifications';
import {
  CREATE_EVENT_CUSTOM_NOTIFICATION,
  UPDATE_EVENT_CUSTOM_NOTIFICATION,
  REMOVE_EVENT_CUSTOM_NOTIFICATION,
  REORDER_EVENT_CUSTOM_NOTIFICATIONS,
  SEND_NOW,
} from 'graphql/mutations/notifications';

import { NotificationStatus } from 'types/enums';
import { CreateCustomNotifForm } from 'components/context/NotificationsContext/interfaces';

const EventNotifPage: FC = () => {
  const { t } = useTranslation();

  const { id = '' } = useParams<keyof EventPageRouteParams>();

  const [sendNow] = useMutation(SEND_NOW, {
    update: (_, { data }) => {
      const sentAt = get(data, 'sendNow.sentAt');

      sentAt && updateNotifSentCache(id, sentAt);
    },
    onCompleted: () => {
      toastNotify({
        text: t('notificationSentSuccessfully'),
      });
    },
    onError,
  });

  const updateNotification = (
    data: Record<'updateEventCustomNotification', CreateCustomNotifForm>
  ) => updateEventNotif(data.updateEventCustomNotification);

  const mutations = useMemo(
    () => ({
      create: CREATE_EVENT_CUSTOM_NOTIFICATION,
      update: UPDATE_EVENT_CUSTOM_NOTIFICATION,
      remove: REMOVE_EVENT_CUSTOM_NOTIFICATION,
      reorder: REORDER_EVENT_CUSTOM_NOTIFICATIONS,
      updateEvent: UPDATE_EVENT,
    }),
    []
  );
  const queries = useMemo(
    () => ({
      notifications: GET_EVENT_CUSTOM_NOTIFICATIONS,
      key: 'eventCustomNotifications',
    }),
    []
  );
  const cache = useMemo(
    () => ({
      removeNotification: removeEventCustomNotifiaction,
      decreaseNotifsCount: () => {
        decreaseEventNotifsCount(id);
      },
      increaseNotifsCount: () => {
        increaseEventNotifsCount(id);
      },
      updateNotification,
    }),
    [id]
  );
  const variables = useMemo(
    () => ({
      eventId: id,
    }),
    [id]
  );
  const refetchQueries = useMemo(() => ['eventCustomNotifications'], []);

  const send = useCallback(
    (customNotificationId: string) => {
      sendNow({
        variables: {
          eventId: id,
          customNotificationId,
        },
        optimisticResponse: {
          sendNow: {
            _id: customNotificationId,
            status: NotificationStatus.sent,
            sentAt: Date.now(),
            __typename: 'EventCustomNotification',
          },
        },
      });
    },
    [id]
  );

  return (
    <NotificationsContextProvider
      value={{
        send,
        mutations,
        queries,
        cache,
        variables,
        refetchQueries,
      }}
    >
      <NotificationsPage />
    </NotificationsContextProvider>
  );
};

export default EventNotifPage;
